import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * @param {CustomEvent & {detail?: {data?: {source?: string}}}} event
   */
  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // NOTE: CartAddEvent.eventName is literally the same string as
    // ThemeEvents.cartUpdate ('cart:update') — see assets/events.js, where
    // `static eventName = ThemeEvents.cartUpdate;`. That means this listener
    // doesn't only fire on real "add to cart" actions: component-cart-items.js
    // dispatches that exact same event name for EVERY cart line change too,
    // including quantity edits and line-item removal (quantity: 0), tagged
    // with `data.source: 'cart-items-component'` (see updateQuantity() in
    // assets/component-cart-items.js). Real "add to cart" submissions are
    // tagged `data.source: 'product-form-component'` instead (see
    // handleSubmit() in assets/product-form.js).
    //
    // We only want to suppress auto-open for the former (removing/adjusting
    // an existing line, e.g. from the /cart page itself, where popping this
    // same drawer open on top of the page is just a redundant duplicate of
    // what's already on screen) — never for the latter (a genuine new item
    // being added, e.g. via a quick-add button in the /cart page's
    // recommendations grid, which SHOULD still open the drawer to confirm).
    if (event?.detail?.data?.source === 'cart-items-component') return;

    this.showDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
